/**
 * StatusStripBlock - Top-level status indicator
 * 
 * Shows risk posture, confidence, last updated, and what changed
 */

import React from 'react'
import { StatusStripBlock as StatusStripBlockType } from '../../../../lib/cockpit/schemas'

interface StatusStripBlockProps {
  data: StatusStripBlockType['data']
  blockId: string
  compact?: boolean
}

const RISK_POSTURE_CONFIG = {
  critical: {
    color: 'bg-red-600',
    textColor: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    label: 'Critical'
  },
  high: {
    color: 'bg-orange-500',
    textColor: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    label: 'High Risk'
  },
  elevated: {
    color: 'bg-amber-500',
    textColor: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    label: 'Elevated'
  },
  moderate: {
    color: 'bg-yellow-500',
    textColor: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    label: 'Moderate'
  },
  low: {
    color: 'bg-blue-500',
    textColor: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    label: 'Low'
  },
  minimal: {
    color: 'bg-green-500',
    textColor: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800',
    label: 'Minimal'
  }
} as const

const CONFIDENCE_CONFIG = {
  very_low: { label: 'Very Low', bars: 1 },
  low: { label: 'Low', bars: 2 },
  medium: { label: 'Medium', bars: 3 },
  high: { label: 'High', bars: 4 },
  very_high: { label: 'Very High', bars: 5 }
} as const

function StatusStripBlock({ data, blockId: _blockId, compact = false }: StatusStripBlockProps) {
  // Default fallbacks for undefined or unknown values
  const defaultRiskConfig = RISK_POSTURE_CONFIG.moderate
  const defaultConfidenceConfig = CONFIDENCE_CONFIG.medium
  
  const riskConfig = data?.risk_posture 
    ? (RISK_POSTURE_CONFIG[data.risk_posture as keyof typeof RISK_POSTURE_CONFIG] || defaultRiskConfig)
    : defaultRiskConfig
  const confidenceConfig = data?.confidence 
    ? (CONFIDENCE_CONFIG[data.confidence as keyof typeof CONFIDENCE_CONFIG] || defaultConfidenceConfig)
    : defaultConfidenceConfig

  // Format the last updated date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      })
    } catch {
      return dateStr
    }
  }

  const lastUpdated = data?.last_updated || new Date().toISOString()

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <div className={`flex items-center gap-1.5 ${riskConfig.textColor}`}>
          <div className={`w-2 h-2 rounded-full ${riskConfig.color}`} />
          <span className="font-medium">{riskConfig.label}</span>
        </div>
        <div className="text-gray-400 dark:text-gray-500">|</div>
        <div className="text-gray-600 dark:text-gray-400">
          Updated {formatDate(lastUpdated)}
        </div>
      </div>
    )
  }

  return (
    <div className={`
      flex flex-wrap items-center gap-4 p-4 rounded-xl border
      ${riskConfig.bgColor} ${riskConfig.borderColor}
    `}>
      {/* Risk Posture */}
      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${riskConfig.color}`} />
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Risk Posture
          </div>
          <div className={`font-semibold ${riskConfig.textColor}`}>
            {riskConfig.label}
          </div>
        </div>
      </div>

      <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />

      {/* Confidence */}
      <div className="flex items-center gap-2">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map(bar => (
            <div
              key={bar}
              className={`w-1.5 h-4 rounded-sm transition-colors ${
                bar <= confidenceConfig.bars
                  ? 'bg-blue-500'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Confidence
          </div>
          <div className="font-medium text-gray-900 dark:text-white">
            {confidenceConfig.label}
          </div>
        </div>
      </div>

      <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />

      {/* Last Updated */}
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Last Updated
        </div>
        <div className="font-medium text-gray-900 dark:text-white">
          {formatDate(lastUpdated)}
        </div>
      </div>

      {/* What Changed */}
      {data?.what_changed && (
        <>
          <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              What Changed
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {data.what_changed}
            </div>
          </div>
        </>
      )}

      {/* Tags */}
      {data?.tags && data.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {data.tags.map((tag: string) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-white/60 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default StatusStripBlock



